import { useContext, useEffect, useState } from "react";
import {
  Button,
  Header,
  Icon,
  Form,
  Image,
  Grid,
  Divider,
  Dropdown,
} from "semantic-ui-react";
import { FileUploader } from "react-drag-drop-files";
import { useLazyQuery, useMutation } from "@apollo/client";
import Resizer from "react-image-file-resizer";
import axios from "axios";
import { CitySelect, StateSelect } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import {
  GET_UPLOAD_LINK,
  GET_DOWNLOAD_LINK,
  UPDATE_USER,
  SERVICES,
  UPDATE_SERVICES,
  CREATE_FILE,
  FILES,
  PREFERRED_REPORTER_SEARCH,
} from "../graphQueries";
import { AuthContext } from "../AuthContext";
import { formatPhoneNumber } from "../Common/functions";
import { toast } from "react-toastify";

const Profile = (props) => {
  const { handleCreateLoginContext } = props;
  const { loginContext } = useContext(AuthContext);
  const isAdmin = loginContext.role === "Admin";
  const isCSR = loginContext.role === "CSR";
  const isAttorney = loginContext.role === "Attorney";
  const isVideographer = loginContext.role === "Videographer";

  // File and preview state
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [w9File, setW9File] = useState(null);
  const [w9Preview, setW9Preview] = useState("");
  // Other form state
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [updateObj, setUpdateObj] = useState({});
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [servicesObj, setServices] = useState({});
  const [preferredReporters, setPreferredReporters] = useState([]);
  const [expeditePercents, setExpeditePercents] = useState({
    sameDay: "",
    oneDay: "",
    twoDay: "",
    threeDay: "",
    fourDay: "",
    fiveDay: "",
    sixDay: "",
    sevenDay: "",
    eightDay: "",
  });
  const fileTypes = ["JPG", "JPEG", "PNG", "GIF"];

  const calcExpeditePercent = (perPage, base) => {
    if (!base || isNaN(base) || !perPage) return "";
    return Math.round(
      (parseFloat(perPage) / parseFloat(base)) * 100
    ).toString();
  };
  const calcExpediteValue = (percent, base) => {
    if (!base || isNaN(base) || !percent) return "";
    return (parseFloat(base) * (parseFloat(percent) / 100)).toFixed(2);
  };

  useEffect(() => {
    const base = parseFloat(servicesObj.originalTranscriptPlusOneCopy || 0);

    setExpeditePercents({
      sameDay: calcExpeditePercent(servicesObj.sameDateExpedite, base),
      oneDay: calcExpeditePercent(servicesObj.oneDayExpedite, base),
      twoDay: calcExpeditePercent(servicesObj.twoDayExpedite, base),
      threeDay: calcExpeditePercent(servicesObj.threeDayExpedite, base),
      fourDay: calcExpeditePercent(servicesObj.fourDayExpedite, base),
      fiveDay: calcExpeditePercent(servicesObj.fiveDayExpedite, base),
      sixDay: calcExpeditePercent(servicesObj.sixDayExpedite, base),
      sevenDay: calcExpeditePercent(servicesObj.sevenDayExpedite, base),
      eightDay: calcExpeditePercent(servicesObj.eightDayExpedite, base),
    });
    // eslint-disable-next-line
  }, [
    servicesObj.originalTranscriptPlusOneCopy,
    servicesObj.sameDateExpedite,
    servicesObj.oneDayExpedite,
    servicesObj.twoDayExpedite,
    servicesObj.threeDayExpedite,
    servicesObj.fourDayExpedite,
    servicesObj.fiveDayExpedite,
    servicesObj.sixDayExpedite,
    servicesObj.sevenDayExpedite,
    servicesObj.eightDayExpedite,
  ]);

  // GraphQL
  const [getUploadLink] = useLazyQuery(GET_UPLOAD_LINK);
  const [getPhotoDownloadLink] = useLazyQuery(GET_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
  });
  const [getW9DownloadLink] = useLazyQuery(GET_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
  });
  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: (result) => handleUpdateUser_Result(result),
    onError: (error) => handleUpdateUser_Error(error),
  });
  const [createFile] = useMutation(CREATE_FILE, {
    onCompleted: () => toast.success("File uploaded!"),
    onError: (error) => toast.error(error.message),
  });
  const [services] = useLazyQuery(SERVICES, {
    fetchPolicy: "network-only",
    onCompleted: (result) => handleServices_Result(result),
    onError: () => {},
  });
  const [updateServices] = useMutation(UPDATE_SERVICES, {
    onCompleted: (result) => handleUpdateServices_Result(result),
    onError: (error) => handleUpdateServices_Error(error),
  });
  const [getPreferredReporters] = useLazyQuery(PREFERRED_REPORTER_SEARCH, {
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      if (result?.preferredReporterSearch) {
        setPreferredReporters(result.preferredReporterSearch);
      }
    },
    onError: (error) => toast.error(error.message),
  });

  // --- NEW: getUserFiles hook at top level ---
  const [getUserFiles] = useLazyQuery(FILES, {
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      if (!result?.files?.length) {
        setProfilePhotoPreview("");
        setW9Preview("");
        return;
      }
      // Profile photo
      const profilePhotoDb = result.files.find(
        (f) => f.type === "profile_photo"
      );
      if (profilePhotoDb) {
        getPhotoDownloadLink({
          variables: { file: profilePhotoDb.path },
          onCompleted: (result) => {
            if (result?.getDownloadLink?.link)
              setProfilePhotoPreview(result.getDownloadLink.link);
          },
          onError: (error) => toast.error(error.message),
        });
      } else {
        setProfilePhotoPreview("");
      }
      // W9
      const w9Db = result.files.find((f) => f.type === "w9");
      if (w9Db) {
        getW9DownloadLink({
          variables: { file: w9Db.path },
          onCompleted: (result) => {
            if (result?.getDownloadLink?.link)
              setW9Preview(result.getDownloadLink.link);
          },
          onError: (error) => toast.error(error.message),
        });
      } else {
        setW9Preview("");
      }
    },
    onError: (error) => {
      setProfilePhotoPreview("");
      setW9Preview("");
      console.error("FILES query error", error);
    },
  });

  useEffect(() => {
    if (!loginContext.userId) return;

    // Fetch user files (profile photo and w9)
    getUserFiles({
      variables: { userId: loginContext.userId },
    });

    if (loginContext.stateId)
      setSelectedState(JSON.parse(loginContext.stateId));
    if (loginContext.cityId) setSelectedCity(JSON.parse(loginContext.cityId));
    setUpdateObj(loginContext);
    if (isCSR || isVideographer) services();
    // eslint-disable-next-line
    if (isAttorney) getPreferredReporters();
  }, [loginContext.userId]);

  // --- File Handlers ---

  const handleChangeProfilePhoto = async (file) => {
    setDisabled(false);
    const fileExt = file.type.split("/")[1];
    const image = await resizeImage(file, fileExt);
    const newFile = renameFile(image, `profile_photo.${fileExt}`);
    setProfilePhotoFile(newFile);
    setProfilePhotoPreview(URL.createObjectURL(newFile));
  };

  const handleChangeW9 = (file) => {
    setDisabled(false);
    setW9File(file);
    setW9Preview(URL.createObjectURL(file));
  };
  const resizeImage = (file, ext) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function (event) {
        const img = new window.Image();
        img.onload = function () {
          // Calculate crop (center square)
          const minDim = Math.min(img.width, img.height);
          const cropX = (img.width - minDim) / 2;
          const cropY = (img.height - minDim) / 2;

          // Create canvas
          const canvas = document.createElement("canvas");
          canvas.width = 300;
          canvas.height = 300;
          const ctx = canvas.getContext("2d");

          // Draw cropped+resized
          ctx.drawImage(
            img,
            cropX,
            cropY,
            minDim,
            minDim, // source crop
            0,
            0,
            300,
            300 // destination size
          );

          // Export as blob, wrap as File for compatibility
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error("Canvas export failed"));
              const outFile = new File([blob], `profile_photo.${ext}`, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(outFile);
            },
            file.type,
            0.98 // quality, for JPEG/WEBP
          );
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const renameFile = (file, newName) => {
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  // --- Unified S3/File upload handler ---
  const handleUploadFile = async ({
    file,
    type,
    path,
    filename,
    onSuccess,
  }) => {
    // 1. Get upload link
    const { data } = await getUploadLink({ variables: { file: path } });
    // 2. Upload file to S3
    await axios.put(data.getUploadLink.link, file, {
      headers: { "Content-Type": file.type },
    });
    // 3. Create DB record
    await createFile({
      variables: {
        userId: loginContext.userId,
        type,
        path,
        filename,
      },
    });
    // 4. Optionally update preview
    if (onSuccess) onSuccess();
  };

  // --- Main save handler ---
  const handleUpdate = async () => {
    setLoading(true);
    setDisabled(true);
    try {
      // Profile Photo upload
      if (profilePhotoFile) {
        const fileExt = profilePhotoFile.type.split("/")[1];
        const filePath = `profiles/${loginContext.userId}/profile_photo.${fileExt}`;
        await handleUploadFile({
          file: profilePhotoFile,
          type: "profile_photo",
          path: filePath,
          filename: profilePhotoFile.name,
          onSuccess: () => {
            getPhotoDownloadLink({
              variables: { file: filePath },
              onCompleted: (result) => {
                if (result?.getDownloadLink?.link)
                  setProfilePhotoPreview(result.getDownloadLink.link);
              },
              onError: (error) => toast.error(error.message),
            });
          },
        });
      }
      // W9 upload
      if (w9File) {
        const filePath = `profiles/${loginContext.userId}/w9.pdf`;
        await handleUploadFile({
          file: w9File,
          type: "w9",
          path: filePath,
          filename: "w9.pdf",
          onSuccess: () => {
            // Immediately fetch the live preview link from S3
            getW9DownloadLink({
              variables: { file: filePath },
              onCompleted: (result) => {
                if (result?.getDownloadLink?.link) {
                  setW9Preview(result.getDownloadLink.link);
                  setW9File(null); // <--- Reset local file!
                }
              },
              onError: (error) => toast.error(error.message),
            });
          },
        });
      }
      // Update profile info and services
      let updateObject = { ...updateObj };
      if (Object.keys(updateObject).length > 0) {
        Object.assign(updateObject, {
          userId: loginContext.userId,
          phone: updateObj.phone ? updateObj.phone.replace(/\D/g, "") : null,
        });
        updateUser({ variables: updateObject });
      }
      if (isCSR || isVideographer) {
        updateServices({ variables: servicesObj });
      }
      toast.success("Profile updated successfully.");
      resetState();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Form handlers ---
  const handleChange = (e, data) => {
    setDisabled(false);
    let key = data.id;
    let value = data.value;
    if (key === "email") return;
    let obj = { ...updateObj };
    if (key === "hourlyRate") value = data.value.replace(/[^0-9]/g, "");
    if (key === "phone") value = formatPhoneNumber(value);
    Object.assign(obj, { [key]: value });
    setUpdateObj(obj);
  };

  const handleChangeDropdown = (id, selectedObj) => {
    setDisabled(false);
    let obj = { ...updateObj };
    if (id === "city") {
      Object.assign(obj, {
        cityId: JSON.stringify(selectedObj),
        city: selectedObj.name,
      });
    }
    if (id === "state") {
      Object.assign(obj, {
        stateId: JSON.stringify(selectedObj),
        state: selectedObj.name,
      });
    }
    setUpdateObj(obj);
  };

  // const handleChangeServices = (e, data) => {
  //   setDisabled(false);
  //   let obj = { ...servicesObj };
  //   let key = data.id;
  //   let value = data.value
  //     .replace(/[^0-9.]/g, "")
  //     .replace(/^(\d*\.?\d{0,2}).*$/, "$1");
  //   Object.assign(obj, { [key]: value });
  //   setServices(obj);
  // };

  const handleChangeServices = (e, data) => {
    setDisabled(false);
    let obj = { ...servicesObj };
    let key = data.id;

    // Remove all non-digit chars
    let digits = (data.value || "").replace(/\D/g, "");
    // Limit max digits if you want (e.g., 9 for $9,999,999.99)
    if (digits.length > 9) digits = digits.slice(0, 9);

    // Parse as integer cents
    let intVal = parseInt(digits, 10) || 0;
    // Always display as dollars.cents with 2 decimals
    let value = (intVal / 100).toFixed(2);

    Object.assign(obj, { [key]: value });
    setServices(obj);
  };

  const handleServices_Result = (result) => {
    if (result && result.services.length) setServices(result.services[0]);
  };

  const handleUpdateServices_Result = (result) => {
    setLoading(false);
    if (result && result.updateServices.status === "succeeded") {
      resetState();
    }
  };

  const handleUpdateServices_Error = (error) => {
    setLoading(false);
    toast.error(error.message);
    resetState();
  };

  const handleUpdateUser_Result = (result) => {
    setLoading(false);
    if (result && result.updateUser.status === "succeeded") {
      if (result.updateUser.token) {
        localStorage.setItem("token", result.updateUser.token);
        handleCreateLoginContext(result.updateUser.token);
      }
      resetState();
    }
  };

  const handleUpdateUser_Error = (error) => {
    setLoading(false);
    toast.error(error.message);
    resetState();
  };

  const resetState = () => {
    setTimeout(() => {
      setUpdateObj({});
    }, 3000);
  };

  return (
    <div
      style={{
        width: "100vw",
        overflow: "auto",
        backgroundColor: "#F1F1F1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: 0,
        margin: 0,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          background: "white",
          maxWidth: "100%",
          width: "100vw",
          display: "flex",
          flexDirection: "column",
          padding: "2.5rem 2.5rem",
          boxShadow: "0 6px 32px rgba(0,0,0,0.12)",
        }}
      >
        <Header
          as="h1"
          content="Profile"
          subheader="Edit your personal details."
          style={{ marginBottom: 36 }}
        />

        <Grid stackable columns={2} style={{ marginBottom: 40 }}>
          <Grid.Column>
            <div
              style={{
                backgroundColor: "#F1F1F1",
                padding: 20,
                borderRadius: 20,
                boxShadow: "0 3px 3px rgba(0,0,0,0.12)",
              }}
            >
              <Form size="large">
                <Form.Group>
                  <Form.Field>
                    <label>Photo (PNG, JPG)</label>
                    <div style={{ display: "flex" }}>
                      <FileUploader
                        handleChange={handleChangeProfilePhoto}
                        name="profile-photo"
                        types={fileTypes}
                        hoverTitle=" "
                        children={
                          <div
                            style={{
                              cursor: "pointer",
                              borderStyle:
                                profilePhotoFile || profilePhotoPreview
                                  ? ""
                                  : "dashed",
                              flex: 1,
                              height: "150px",
                              width: "150px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              borderRadius: "20px",
                              borderColor: "#888888",
                              padding: "10px",
                              background: "#FFFFFF",
                            }}
                          >
                            {!profilePhotoFile && !profilePhotoPreview && (
                              <Header style={{ textAlign: "" }}>
                                <Icon color="blue" name="image" />
                                <Header.Content>Photo</Header.Content>
                                <Header.Subheader>
                                  Drag image file here, or click to browse.
                                </Header.Subheader>
                              </Header>
                            )}
                            {(profilePhotoFile || profilePhotoPreview) && (
                              <Image
                                style={{
                                  borderRadius: "20px",
                                  maxWidth: "150px",
                                  maxHeight: "150px",
                                  objectFit: "cover",
                                  border: "1px solid #888888",
                                }}
                                src={
                                  profilePhotoFile
                                    ? URL.createObjectURL(profilePhotoFile)
                                    : profilePhotoPreview
                                    ? profilePhotoPreview
                                    : "/images/placeholder.png"
                                }
                              />
                            )}
                          </div>
                        }
                      />
                    </div>
                  </Form.Field>
                  {isCSR && (
                    <Form.Field>
                      <label>W9 Form (PDF)</label>
                      {w9File ? (
                        // 2. Pending upload: Show icon/filename if user picked a file but hasn't saved/uploaded yet
                        <div
                          style={{
                            cursor: "pointer",
                            borderStyle: "dashed", // Match uploader
                            flex: 1,
                            height: "150px", // Match uploader
                            width: "150px", // Match uploader
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "20px",
                            borderColor: "#888888",
                            padding: "10px",
                            background: "#fff",
                          }}
                        >
                          <Icon name="file pdf" size="huge" color="red" />
                          <div style={{ marginTop: 8, fontWeight: "bold" }}>
                            w9.pdf
                          </div>
                        </div>
                      ) : (
                        // 1, 3, 4: Show uploader always otherwise
                        <>
                          <FileUploader
                            handleChange={handleChangeW9}
                            name="w9"
                            types={["PDF"]}
                            hoverTitle=" "
                            children={
                              <div
                                style={{
                                  cursor: "pointer",
                                  borderStyle: "dashed",
                                  flex: 1,
                                  height: "150px",
                                  width: "150px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderRadius: "20px",
                                  borderColor: "#888888",
                                  padding: "10px",
                                  background: "#fff",
                                }}
                              >
                                <Header style={{ textAlign: "" }}>
                                  <Icon name="file pdf" color="red" />
                                  <Header.Content>W9 PDF</Header.Content>
                                  <Header.Subheader>
                                    Drag PDF file here, or click to browse.
                                  </Header.Subheader>
                                </Header>
                              </div>
                            }
                          />
                          {/* 3, 4: If an uploaded file exists, show the view link below uploader */}
                          {w9Preview && (
                            <div style={{ marginTop: 8 }}>
                              <a
                                href={w9Preview}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#2185d0", fontWeight: "bold" }}
                              >
                                View Uploaded W9
                              </a>
                            </div>
                          )}
                        </>
                      )}
                    </Form.Field>
                  )}
                </Form.Group>
                <p
                  style={{
                    fontSize: 11,
                    color: "#888888",
                    fontFamily: "monospace",
                  }}
                >
                  User ID: {updateObj.userId}
                </p>
                <Form.Input
                  id="fullName"
                  label="Full Name"
                  placeholder="Full Name"
                  value={updateObj.fullName}
                  onChange={handleChange}
                />
                {isCSR && (
                  <Form.Input
                    id="dbaName"
                    label="DBA"
                    placeholder="e.g. ABC Court Reporting"
                    value={updateObj.dbaName}
                    onChange={handleChange}
                  />
                )}
                {isAttorney && (
                  <Form.Input
                    id="firmName"
                    label="Law Firm Name"
                    placeholder="e.g. ABC Law Firm"
                    value={updateObj.firmName}
                    onChange={handleChange}
                  />
                )}
                <Form.Input
                  id="email"
                  label="Email"
                  placeholder="Email"
                  value={updateObj.email}
                  onChange={handleChange}
                />
                <Form.Input
                  id="phone"
                  label="Phone"
                  placeholder="Phone"
                  value={updateObj.phone}
                  onChange={handleChange}
                />
                <Form.Input
                  id="street1"
                  label="Address"
                  placeholder="Address"
                  value={updateObj.street1}
                  onChange={handleChange}
                />
                <Form.Input
                  id="street2"
                  label="Address #2"
                  placeholder="Address #2"
                  value={updateObj.street2}
                  onChange={handleChange}
                />
                <Form.Field>
                  <label>State</label>
                  <StateSelect
                    countryid={233}
                    onChange={(state) => {
                      setSelectedState(state);
                      handleChangeDropdown("state", state);
                    }}
                    defaultValue={selectedState}
                    placeHolder="Select State"
                  />
                </Form.Field>
                <Form.Field>
                  <label>City</label>
                  <CitySelect
                    inputClassName="ui input menu transition"
                    placeHolder="Select City"
                    countryid={233}
                    stateid={selectedState?.id}
                    disabled={!selectedState?.id}
                    onChange={(city) => {
                      setSelectedCity(city);
                      handleChangeDropdown("city", city);
                    }}
                    defaultValue={selectedCity}
                  />
                </Form.Field>
                <Form.Input
                  id="zipCode"
                  label="Zip Code"
                  placeholder="Zip Code"
                  value={updateObj.zipCode}
                  onChange={handleChange}
                />
                {isAttorney && (
                  <Form.Input
                    id="billingEmail"
                    label="Billing Email"
                    placeholder="e.g. accounting@lawfirm.com"
                    value={updateObj.billingEmail}
                    onChange={handleChange}
                  />
                )}
              </Form>
            </div>
          </Grid.Column>

          {isAttorney && (
            <Grid.Column>
              <div
                style={{
                  backgroundColor: "#F1F1F1",
                  padding: 20,
                  borderRadius: 20,
                  boxShadow: "0 3px 3px rgba(0,0,0,0.12)",
                }}
              >
                <Form size="large">
                  <Divider horizontal section>
                    Preferred Reporter
                  </Divider>
                  <Form.Field>
                    <label>Select Preferred Reporter</label>
                    <Dropdown
                      fluid
                      selection
                      placeholder="Choose a reporter"
                      options={preferredReporters.map((r) => ({
                        key: r.userId,
                        value: r.userId,
                        text: `${r.fullName} (${r.location})`,
                        image: r.photoLink
                          ? { avatar: true, src: r.photoLink }
                          : undefined,
                      }))}
                      value={updateObj.preferredReporterId || ""}
                      onChange={(e, { value }) => {
                        setDisabled(false);
                        setUpdateObj((prev) => ({
                          ...prev,
                          preferredReporterId: value,
                        }));
                      }}
                    />
                  </Form.Field>
                </Form>
              </div>
            </Grid.Column>
          )}

          {isVideographer && (
            <Grid.Column>
              <div
                style={{
                  backgroundColor: "#F1F1F1",
                  padding: 20,
                  borderRadius: 20,
                  boxShadow: "0 3px 3px rgba(0,0,0,0.12)",
                }}
              >
                <Form size="large">
                  <Divider horizontal section>
                    Service Rates
                  </Divider>
                  <Form.Input
                    label="Videographer - Initial Fee"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="videographerFee"
                    value={servicesObj.videographerFee || ""}
                  />
                  <Form.Input
                    label="Videographer - Hourly Rate"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Hourly</Button>}
                    onChange={handleChangeServices}
                    id="videographerHourly"
                    value={servicesObj.videographerHourly || ""}
                  />
                </Form>
              </div>
            </Grid.Column>
          )}

          {isCSR && (
            <Grid.Column>
              <div
                style={{
                  backgroundColor: "#F1F1F1",
                  padding: 20,
                  borderRadius: 20,
                  boxShadow: "0 3px 3px rgba(0,0,0,0.12)",
                }}
              >
                <Form size="large">
                  <Divider horizontal section>
                    Service Rates
                  </Divider>
                  <Form.Input
                    label="Initial Appearance Fee"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="appearanceFee"
                    value={servicesObj.appearanceFee || ""}
                  />
                  <Form.Input
                    label="Hourly Rate"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Hourly</Button>}
                    onChange={handleChangeServices}
                    id="hourlyRate"
                    value={servicesObj.hourlyRate || ""}
                  />
                  <Form.Input
                    label="Take Down / No Write"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="takeDownNoWrite"
                    value={servicesObj.takeDownNoWrite || ""}
                  />
                  <Form.Input
                    label="Wait Time"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Hourly</Button>}
                    onChange={handleChangeServices}
                    id="waitTime"
                    value={servicesObj.waitTime || ""}
                  />
                  <Form.Input
                    label="After Hours / Weekend"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Hourly</Button>}
                    onChange={handleChangeServices}
                    id="afterHours"
                    value={servicesObj.afterHours || ""}
                  />
                  <Form.Input
                    label="Cancellation Fee"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="cancellationFee"
                    value={servicesObj.cancellationFee || ""}
                  />

                  <Form.Input
                    label="Conference Room"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Day</Button>}
                    onChange={handleChangeServices}
                    id="provideConferenceRoom"
                    value={servicesObj.provideConferenceRoom || ""}
                  />
                  <Form.Input
                    label="Video Conferencing"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Hour</Button>}
                    onChange={handleChangeServices}
                    id="provideVideoConference"
                    value={servicesObj.provideVideoConference || ""}
                  />
                  <Form.Input
                    label="Remote Realtime"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="remoteServices"
                    value={servicesObj.remoteServices || ""}
                  />
                  <Form.Input
                    label="Additional Laptop"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Device</Button>}
                    onChange={handleChangeServices}
                    id="additionalLaptop"
                    value={servicesObj.additionalLaptop || ""}
                  />
                  <Form.Input
                    label="Additional Tablet"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Device</Button>}
                    onChange={handleChangeServices}
                    id="additionalTablet"
                    value={servicesObj.additionalTablet || ""}
                  />
                  <Form.Input
                    label="Certificate of Non-Appearance"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="certificateOfNonappearance"
                    value={servicesObj.certificateOfNonappearance || ""}
                  />
                  <Form.Input
                    label="Courthouse Half Day"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="courthouseHalfDay"
                    value={servicesObj.courthouseHalfDay || ""}
                  />
                  <Form.Input
                    label="Courthouse Full Day"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="courthouseFullDay"
                    value={servicesObj.courthouseFullDay || ""}
                  />
                  <Form.Input
                    label="Courthouse Hourly"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Hour</Button>}
                    onChange={handleChangeServices}
                    id="courthouseHourly"
                    value={servicesObj.courthouseHourly || ""}
                  />
                  <Form.Input
                    label="Notary Public"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="notaryPublic"
                    value={servicesObj.notaryPublic || ""}
                  />
                  <Form.Input
                    label="Delivery"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="deliveryHandling"
                    value={servicesObj.deliveryHandling || ""}
                  />
                  <Form.Input
                    label="Mileage"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Mile</Button>}
                    onChange={handleChangeServices}
                    id="mileage"
                    value={servicesObj.mileage || ""}
                  />
                  <Divider horizontal section>
                    Transcript & Exhibits
                  </Divider>
                  <Form.Input
                    label="Original Transcript (1 Copy)"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="originalTranscriptPlusOneCopy"
                    value={servicesObj.originalTranscriptPlusOneCopy || ""}
                  />
                  <Form.Input
                    label="Index Fee"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="indexFee"
                    value={servicesObj.indexFee || ""}
                  />
                  <Form.Input
                    label="Minimum Original Transcript Fee"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Fixed</Button>}
                    onChange={handleChangeServices}
                    id="minimumOriginalTranscriptFee"
                    value={servicesObj.minimumOriginalTranscriptFee || ""}
                  />
                  <Form.Input
                    label="Additional Copy"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="copyTranscript"
                    value={servicesObj.copyTranscript || ""}
                  />
                  <Form.Input
                    label="Medical/Expert"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="medicalExpert"
                    value={servicesObj.medicalExpert || ""}
                  />
                  <Form.Input
                    label="Hearings/Arbitrations"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="hearingsArbitrations"
                    value={servicesObj.hearingsArbitrations || ""}
                  />
                  <Form.Input
                    label="Trials"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="trials"
                    value={servicesObj.trials || ""}
                  />
                  <Form.Input
                    label="Videotaped"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="videoTaped"
                    value={servicesObj.videoTaped || ""}
                  />

                  <Form.Input
                    label="Interpreted"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="interpreted"
                    value={servicesObj.interpreted || ""}
                  />

                  <Form.Input
                    label="Audio Transcription"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="audioTranscript"
                    value={servicesObj.audioTranscript || ""}
                  />

                  <Form.Input
                    label="Realtime Transcript"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="realtime"
                    value={servicesObj.realtime || ""}
                  />

                  <Form.Input
                    label="CART"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="cart"
                    value={servicesObj.cart || ""}
                  />

                  <Form.Input
                    label="Rough Draft"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="roughDraft"
                    value={servicesObj.roughDraft || ""}
                  />

                  <Form.Input
                    label="ETran"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="etran"
                    value={servicesObj.etran || ""}
                  />

                  <Form.Input
                    label="Exhibit Copy (Black & White)"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="exhibitCopyBlackAndWhite"
                    value={servicesObj.exhibitCopyBlackAndWhite || ""}
                  />
                  <Form.Input
                    label="Exhibit Copy (Color)"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="exhibitCopyColor"
                    value={servicesObj.exhibitCopyColor || ""}
                  />
                  <Form.Input
                    label="VTC Surcharge"
                    icon="dollar"
                    iconPosition="left"
                    placeholder="0.00"
                    action={<Button disabled>Per Page</Button>}
                    onChange={handleChangeServices}
                    id="vtcSurcharge"
                    value={servicesObj.vtcSurcharge || ""}
                  />
                  <Divider horizontal section>
                    Expedites
                  </Divider>
                  <p>
                    You can enter the exact price per page for Expedites, or
                    enter the percentage of the original transcript per-page
                    rate, and it will be calculated for you.
                  </p>

                  {[
                    {
                      label: "Same Day Expedite",
                      id: "sameDateExpedite",
                      key: "sameDay",
                    },
                    {
                      label: "1 Day Expedite",
                      id: "oneDayExpedite",
                      key: "oneDay",
                    },
                    {
                      label: "2 Day Expedite",
                      id: "twoDayExpedite",
                      key: "twoDay",
                    },
                    {
                      label: "3 Day Expedite",
                      id: "threeDayExpedite",
                      key: "threeDay",
                    },
                    {
                      label: "4 Day Expedite",
                      id: "fourDayExpedite",
                      key: "fourDay",
                    },
                    {
                      label: "5 Day Expedite",
                      id: "fiveDayExpedite",
                      key: "fiveDay",
                    },
                    {
                      label: "6 Day Expedite",
                      id: "sixDayExpedite",
                      key: "sixDay",
                    },
                    {
                      label: "7 Day Expedite",
                      id: "sevenDayExpedite",
                      key: "sevenDay",
                    },
                    {
                      label: "8 Day Expedite",
                      id: "eightDayExpedite",
                      key: "eightDay",
                    },
                  ].map(({ label, id, key }) => (
                    <Form.Group widths="equal" key={id}>
                      <Form.Input
                        label={`${label}`}
                        icon="dollar"
                        iconPosition="left"
                        placeholder="0.00"
                        id={id}
                        value={servicesObj[id] || ""}
                        onChange={(e, data) => {
                          handleChangeServices(e, data);
                          // Update corresponding % based on value
                          const base = parseFloat(
                            servicesObj.originalTranscriptPlusOneCopy || 0
                          );
                          setExpeditePercents((prev) => ({
                            ...prev,
                            [key]: calcExpeditePercent(data.value, base),
                          }));
                        }}
                        action={<Button disabled>Per Page</Button>}
                      />
                      <Form.Input
                        label="% of Original Transcript Rate"
                        type="number"
                        min={0}
                        max={1000}
                        step={0.1}
                        placeholder="0"
                        value={expeditePercents[key]}
                        onChange={(e) => {
                          // Use the raw input value
                          const percentRaw = e.target.value;
                          // Allow blank, partial, or full string values for percent field (uncontrolled during typing)
                          setExpeditePercents((prev) => ({
                            ...prev,
                            [key]: percentRaw,
                          }));
                          // Only calc and set the $/page if percent is a finite number and base exists
                          const base = parseFloat(
                            servicesObj.originalTranscriptPlusOneCopy || 0
                          );
                          const percentNum = parseFloat(percentRaw);
                          if (
                            !isNaN(percentNum) &&
                            percentRaw !== "" &&
                            base > 0
                          ) {
                            const value = calcExpediteValue(percentNum, base);
                            setServices((obj) => ({
                              ...obj,
                              [id]: value,
                            }));
                          }
                        }}
                        icon={<Icon name="percent" />}
                        iconPosition="right"
                      />
                    </Form.Group>
                  ))}
                </Form>
              </div>
            </Grid.Column>
          )}
        </Grid>
      </div>
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          background:
            "linear-gradient(to top, rgba(241,241,241,0.98) 80%, rgba(241,241,241,0.0))",
          padding: "1.5rem 0 1.5rem 0",
          // boxShadow: "0 -3px 18px rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 300,
            pointerEvents: "auto",
            padding: "0 2rem",
          }}
        >
          <Button
            icon="save"
            disabled={disabled}
            loading={loading}
            onClick={handleUpdate}
            content="Save Changes"
            positive
            size="large"
          />
        </div>
      </div>
    </div>
  );
};

export default Profile;
